import {ActivatedRoute, Router} from '@angular/router';
import {CommonService} from '../../services/common.service';
import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatSelect} from '@angular/material';
import {ReplaySubject, Subject} from 'rxjs';
import {ToastMessagesService} from '../../services/toast-messages.service';
import {take, takeUntil} from 'rxjs/operators';

//#region local interfaces
interface Type {
  id: number;
  alternate_description: string;
  description: string;
  type_id?: number;
}

interface Option {
  id: number;
  description: string;
}

interface Question {
  id: number;
  description: string;
  type?: Type;
  isRequired?: boolean;
  options?: Option[];
  optionSearchValue?: any;
}

interface QuestionnaireCategory {
  id: number;
  description: string;
}

//#endregion

@Component({
  templateUrl: './questionnaire-creator.component.html'
})
export class QuestionnaireCreatorComponent implements OnInit {
  pageTitle: string;
  categories: any;
  categoriesLoading: boolean;
  selectedCategory: QuestionnaireCategory;
  title: string;
  questions = [];
  questionnaireQuestions: Question[] = [];
  loadingQuestions: boolean;
  selectedQuestion: any;
  questionTypes = [];
  selectedType: any;
  loadingTypes: boolean;
  options: Option[];
  isRequired: boolean;
  buttonMessage: string;
  questionCtrl: FormControl = new FormControl();
  questionFilterCtrl: FormControl = new FormControl();
  filteredQuestions: ReplaySubject<Question[]> = new ReplaySubject<Question[]>(1);
  details: any;
  mode: number;
  detailsQuestions: Question[];

  @ViewChild('singleSelect', {static: true}) singleSelect: MatSelect;

  // tslint:disable-next-line:variable-name
  protected _onDestroy = new Subject<void>();

  constructor(private activatedRoute: ActivatedRoute,
              private commonService: CommonService,
              private router: Router,
              private toastMessagesService: ToastMessagesService) {
    this.mode = 0;
    this.categoriesLoading = false;
    this.loadingQuestions = false;
    this.loadingTypes = false;
    this.title = '';
    this.isRequired = false;
  }

  public async ngOnInit(): Promise<any> {
    await this.initCategories();
    await this.initQuestions();
    await this.initTypes();
    await this.initOptions();
    this.activatedRoute.queryParams.subscribe(async (params) => {
      if (params.id) {
        this.mode = 1;
        this.pageTitle = 'Ενημέρωση ερωτηματολογίου';
        this.buttonMessage = 'Ενημέρωση ερωτηματολογίου';
        await this.getDetails(+params.id);
        if (this.details) {
          this.details.category = {
            id: this.details.category.category_id,
            description: this.details.category.category_description
          };

          this.title = this.details.title;
          const category = this.categories.find(c => c.id === this.details.category.id);
          this.selectedCategory = category ? category : null;
          this.detailsQuestions = this.details.questions as Question[];

          for (const question of this.detailsQuestions) {
            const q = this.questions.find(quest => quest.id === +question.id);
            question.type.id = question.type.type_id;
            if (q) {
              q.isRequired = question.isRequired;
              // q.options = question.options;

              for (const option of question.options) {
                const currentOption = this.options.find(o => o.id === option.id);
                q.options.push(currentOption);
              }

              q.type = this.questionTypes.find(type => type.id === question.type.id);
              this.questionnaireQuestions.push(q);
            }
          }

        }
      } else {
        this.pageTitle = 'Δημιουργία ερωτηματολογίου';
        this.buttonMessage = this.pageTitle;
      }

    });


    this.filteredQuestions
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.singleSelect.compareWith = (a: Question, b: Question) => a && b && a.id === b.id;
      });

    this.questionFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterQuestions();
    });
  }

  protected filterQuestions() {
    if (!this.questions) {
      return;
    }

    let search = this.questionFilterCtrl.value;
    if (!search) {
      this.filteredQuestions.next(this.questions.slice());
    } else {
      search = search.toLowerCase();
    }

    this.filteredQuestions.next(this.questions.filter(question => question.description.toLowerCase().indexOf(search) > -1));
  }

  async initOptions() {
    try {
      this.loadingQuestions = true;
      this.options = await this.commonService.getList('questionnaire/option') as Option[];
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.loadingQuestions = false;
    }
  }

  async initQuestions() {
    try {
      this.loadingQuestions = true;
      this.questions = await this.commonService.getList('questionnaire/question') as [];
      for (const question of this.questions) {
        question.options = [];
      }
      this.questionCtrl.setValue(this.questions[0]);
      this.filteredQuestions.next(this.questions.slice());


    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.loadingQuestions = false;
    }
  }

  async initTypes() {
    try {
      this.loadingTypes = true;
      this.questionTypes = await this.commonService.getList('questionnaire/type') as Type[];
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.loadingTypes = false;
    }
  }

  async initCategories() {
    this.categoriesLoading = true;
    try {
      this.categories = await this.commonService.getList(`questionnaire/category`);
    } catch (e) {
      this.showError(e);
    } finally {
      this.categoriesLoading = false;
    }
  }

  async getDetails(id: number) {
    try {
      this.details = await this.commonService.getList(`questionnaire/${id}`);
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  showError(error) {
    this.toastMessagesService.toastErrorMessage(error.message);
  }

  addQuestion() {
    if (this.selectedQuestion) {
      if (!this.selectedType) {
        this.toastMessagesService.toastMessages(`Θα πρέπει να επιλέξετε τον τύπο της ερώτησης για να την προσθέσετε στο ερωτηματολόγιο`);
        return false;
      }
      const question: Question = {
        id: this.selectedQuestion.id,
        description: this.selectedQuestion.description,
        isRequired: this.isRequired,
        type: this.selectedType,
        options: []
      };
      this.questionnaireQuestions.push(question);
      this.clearQuestionFields();
    } else {
      this.toastMessagesService.toastMessages(`Θα πρέπει να επιλέξετε μία ερώτηση για να την προσθέσετε στο ερωτηματολόγιο`);
    }
  }

  insertAnswer(question: Question, update?: boolean) {

    if (!question.optionSearchValue) {
      this.toastMessagesService.toastMessages(`Θα πρέπει να επιλέξετε μία απάντηση για να την προσθέσετε στην ερώτηση`);
      return false;
    } else {
      if (!update) {
        question.options.push(question.optionSearchValue);
        question.optionSearchValue = null;
      } else {
        if (question.options.length === 0) {
          question.options.push(question.optionSearchValue);
          question.optionSearchValue = null;
        } else {
          question.options[0] = question.optionSearchValue;
          question.optionSearchValue = null;
        }
      }
    }
  }

  clearQuestionFields() {
    this.selectedType = null;
    this.selectedQuestion = null;
    this.isRequired = false;
  }

  async saveQuestionnaire() {
    if (!this.title || !this.selectedCategory) {
      this.toastMessagesService.toastMessages(`Ελέγξτε αν έχετε όλα τα πεδία συμπληρωμένα. Τίτλος Κατηγορία και αν έχετε προσθέσει ερωτήσεις`);
      return false;
    }

    const insertModel = {
      description: this.title,
      category_id: this.selectedCategory.id,
      status_id: null,
      questions: []
    };

    for (const question of this.questionnaireQuestions) {
      const quest = {
        id: question.id,
        is_required: question.isRequired ? 1 : 0,
        type_id: question.type.id,
        options: []
      };

      if (question.options.length === 0) {
        this.toastMessagesService.toastMessages(`Όλες οι ερωτήσεις θα πρέπει να έχουν τουλάχιστον 1 επιλογή απάντησης`);
        return false;
      }
      for (const option of question.options) {
        quest.options.push(option.id);
      }

      insertModel.questions.push(quest);
    }

    try {
      if (this.mode === 0) {
        await this.commonService.insertItem(`questionnaire`, insertModel);
        this.toastMessagesService.toastMessages(`Το ερωτηματολόγιο δημιουργήθηκε με επιτυχία`);
        await this.router.navigate(['questionaires']);
      } else {
        await this.commonService.updateObject(`questionnaire/${this.details.id}`, insertModel);
        this.toastMessagesService.toastMessages(`Το ερωτηματολόγιο ενημερώθηκε με επιτυχία`);
        await this.router.navigate(['questionaires']);
      }
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  removeOption(option: Option, options: Option[]) {
    const index = options.indexOf(option);
    if (index !== -1) {
      options.splice(index, 1);
    }
  }

  removeQuestion(question: Question) {
    const index = this.questionnaireQuestions.indexOf(question);
    if (index !== -1) {
      this.questionnaireQuestions.splice(index, 1);
    }
  }

  updateOptionInQuestion(question: Question, i: number, option: Option) {
    question.options[i] = option;
  }
}
